import React, { useState, useRef, useEffect } from 'react'
import type { DropResult, DragStart } from '@hello-pangea/dnd'
import { DragDropContext } from '@hello-pangea/dnd'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import type { Deal, DealSummaryResponse } from '@paintscout/api'
import BoardViewColumn from './BoardViewColumn'
import 'animate.css'

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {
      position: 'relative'
    },
    droppableContainer: {
      display: 'flex',
      flexDirection: 'row',
      overflow: 'auto',
      position: 'relative',
      maxHeight: 'calc(100dvh - 205px)',
      fallbacks: [{ maxHeight: 'calc(100vh - 205px)' }],
      [theme.breakpoints.down('sm')]: {
        maxHeight: 'calc(100dvh - 260px)',
        fallbacks: [{ maxHeight: 'calc(100vh - 260px)' }]
      }
    },
    fade: {
      position: 'absolute',
      top: 0,
      bottom: 0,
      width: 40,
      pointerEvents: 'none',
      zIndex: 101
    },
    fadeLeft: {
      left: 0,
      background: `linear-gradient(to right, ${theme.palette.background.default}, transparent)`
    },
    fadeRight: {
      right: 0,
      background: `linear-gradient(to left, ${theme.palette.background.default}, transparent)`
    }
  }),
  { name: 'BoardView' }
)

export interface BoardViewProps {
  columns: Array<{
    label: string
    key: string
  }>
  contentsComponent: React.FC
  data: BoardViewData
  keyField: string
  onDragEnd?: (result: DropResult, updatedData: BoardViewData) => void
  pipeline: string
  loadingStages?: string[]
  fetchingMore?: string[]
  hasMoreStages?: string[]
  hasErrors?: string[]
  onLoadMore: (stageValue: string) => void
  newLeadCount?: number
  onMarkNewLeadsSeen?: () => void
}

export interface BoardViewData {
  [key: string]: {
    deals: Deal[]
    aggregates: DealSummaryResponse
  }
}

function BoardView(props: BoardViewProps) {
  const classes = useStyles(props)
  const {
    columns,
    contentsComponent,
    data,
    keyField,
    onDragEnd,
    pipeline,
    loadingStages = [],
    fetchingMore = [],
    hasMoreStages = [],
    hasErrors = [],
    onLoadMore,
    newLeadCount = 0,
    onMarkNewLeadsSeen
  } = props
  const [sourceId, setSourceId] = useState<string | null>(null)
  const [showLeftFade, setShowLeftFade] = useState(false)
  const [showRightFade, setShowRightFade] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = containerRef.current
        setShowLeftFade(scrollLeft > 0)
        setShowRightFade(scrollLeft + clientWidth < scrollWidth - 10)
      }
    }

    const container = containerRef.current
    if (container) {
      handleScroll() // Initialize fade visibility
      container.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  return (
    <div className={classes.root}>
      {showLeftFade && <div className={`${classes.fade} ${classes.fadeLeft}`} />}
      {showRightFade && <div className={`${classes.fade} ${classes.fadeRight}`} />}
      <div className={classes.droppableContainer} ref={containerRef}>
        <DragDropContext onDragStart={handleDragStart} onDragEnd={handleOnDragEnd}>
          {columns?.map((column, index) => {
            const showLeadCallout = index === 0
            const columnLeadCount = showLeadCallout ? newLeadCount : 0

            return (
              <BoardViewColumn
                key={column.key}
                keyField={keyField}
                column={column}
                data={data?.[column.key]}
                ContentsComponent={contentsComponent}
                isDropDisabled={column.key === sourceId}
                pipeline={pipeline}
                onLoadMore={() => onLoadMore(column.key)}
                isLoading={loadingStages.includes(column.key)}
                isFetchingMore={fetchingMore.includes(column.key)}
                hasMore={hasMoreStages?.includes(column.key)}
                hasError={hasErrors?.includes(column.key)}
                displayLeadCallout={showLeadCallout}
                newLeadCount={columnLeadCount}
                onMarkNewLeadsSeen={showLeadCallout ? onMarkNewLeadsSeen : undefined}
              />
            )
          })}
        </DragDropContext>
      </div>
    </div>
  )

  function handleDragStart(dragStart: DragStart) {
    setSourceId(dragStart.source.droppableId)
  }

  async function handleOnDragEnd(dropResult: DropResult) {
    setSourceId(null)
    const { source, destination, draggableId } = dropResult

    if (!destination) {
      return
    }

    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return
    }

    const updatedData = updateData({ source, destination, draggableId, data })

    return onDragEnd(dropResult, updatedData)
  }

  function updateData({
    source,
    destination,
    draggableId: _draggableId,
    data
  }: {
    source: { droppableId: string; index: number }
    destination: { droppableId: string; index: number }
    draggableId: string
    data: BoardViewData
  }): BoardViewData {
    // console.log({ source, destination })
    if (source.droppableId === destination.droppableId) {
      // moving within same column - disabled for now
      return data

      // const column = data[source.droppableId]

      // const [item] = column.splice(source.index, 1)
      // column.splice(destination.index ?? column.length + 1, 0, item)

      // return {
      //   ...data,
      //   [destination.droppableId]: column
      // }
    } else {
      // moving across columns

      const sourceColumn = data[source.droppableId]
      const destinationColumn = data[destination.droppableId] || {
        deals: [],
        aggregates: { totalDeals: 0, totalDealValue: 0 }
      }

      const [item] = sourceColumn.deals.splice(source.index, 1)
      destinationColumn.deals.splice(destination.index ?? destinationColumn.deals.length + 1, 0, item)

      item.stage = {
        value: destination.droppableId
      }

      return {
        ...data, // unchanged columns
        [source.droppableId]: sourceColumn, // updated source column
        [destination.droppableId]: destinationColumn // updated destination column
      }
    }
  }
}

export default BoardView
